// Copyright (c) 2026 Goffy. All Rights Reserved.

// Detects JS-thread freezes via heartbeat drift: a 500 ms interval whose
// actual gap exceeds the expected gap by >100 ms means the only thread that
// runs timers was blocked. Works identically on Hermes, JSC and web, unlike
// rAF (throttled when hidden) or PerformanceObserver('longtask') (web-only).

import { AppState, Platform } from 'react-native';
import { metricsEnabled, nowMs, recordHistogram } from './appMetrics';

const TICK_INTERVAL_MS = 500;
const BLOCKED_THRESHOLD_MS = 100;

let timer: ReturnType<typeof setInterval> | null = null;
let lastTickAt = 0;
let skipNextTick = false;
let lifecycleHooked = false;

function tick() {
  const now = nowMs();
  const drift = now - lastTickAt - TICK_INTERVAL_MS;
  lastTickAt = now;

  if (skipNextTick) {
    skipNextTick = false;
    return;
  }

  // Hidden web tabs throttle timers to >=1 s; those gaps are not freezes.
  if (Platform.OS === 'web' && typeof document !== 'undefined' && document.hidden) {
    skipNextTick = true;
    return;
  }

  if (drift > BLOCKED_THRESHOLD_MS) {
    recordHistogram('goffy.frontend.jsthread.blocked.duration', drift);
  }
}

export function startJsThreadMonitor(): void {
  if (!metricsEnabled || timer !== null) {
    return;
  }

  lastTickAt = nowMs();
  skipNextTick = false;
  timer = setInterval(tick, TICK_INTERVAL_MS);

  if (lifecycleHooked) {
    return;
  }
  lifecycleHooked = true;

  if (Platform.OS === 'web') {
    if (typeof document !== 'undefined') {
      // The first tick after returning from a hidden tab spans the throttled
      // period and would read as a giant fake freeze.
      document.addEventListener('visibilitychange', () => {
        skipNextTick = true;
        lastTickAt = nowMs();
      });
    }
  } else {
    // Native timers are suspended in the background; same fake-gap problem.
    AppState.addEventListener('change', () => {
      skipNextTick = true;
      lastTickAt = nowMs();
    });
  }
}

export function stopJsThreadMonitor(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}
